import { memo, useCallback, useMemo } from "react";
import { Alert, Stack, TextField } from "@mui/material";
import { TDocumentData } from "../../models";
import { saveDocumentDataForDocument } from "../../network";
import { cleanupConfigFieldValues } from "../../utils/cleanupConfigFieldValues";
import { getEditablePrimaryDocumentData } from "../../utils/pendingDocument";
import { AccordionShell } from "../StepAccordion/AccordionShell";
import { BinaryContent } from "./index";
import type { DocumentTypeRendererProps } from "./DocumentTypeRenderer";

function splitData(data: TDocumentData[]) {
  const binary: TDocumentData[] = [];
  const file: TDocumentData[] = [];
  for (const item of data) {
    if (item.type === "binary") binary.push(item);
    else if (item.type === "file") file.push(item);
  }
  return { binary, file };
}

export const UserInformationRenderer = memo(
  (props: DocumentTypeRendererProps) => {
    const {
      document,
      requestId,
      title,
      configFields,
      companyRole,
      canEdit,
      comments,
      addComment,
      userInfo,
      isMobile,
      onRefreshDocuments,
      defaultExpanded,
    } = props;
    const { binary, file } = useMemo(
      () => splitData(document.data),
      [document.data],
    );
    const documentType = document.document.documentType;
    const loadedMainEntry = useMemo(
      () => binary.find((d) => d.dataId === documentType),
      [binary, documentType],
    );
    const mainEntry = useMemo(
      () =>
        loadedMainEntry ??
        (canEdit ? getEditablePrimaryDocumentData(document) : undefined),
      [canEdit, document, loadedMainEntry],
    );

    const canEditPayload = canEdit && !!mainEntry;
    const initialValues = useMemo(
      () => (mainEntry?.data as Record<string, unknown> | undefined) ?? {},
      [mainEntry?.data],
    );
    const mainDataId = mainEntry?.dataId;
    const onSavePayload = useCallback(
      async (values: Record<string, unknown>) => {
        if (!mainDataId) return;
        values = cleanupConfigFieldValues(values, configFields);
        const effectiveDocument = await saveDocumentDataForDocument(
          document.document,
          requestId,
          mainDataId,
          values,
        );
        return effectiveDocument.documentId;
      },
      [configFields, document.document, mainDataId, requestId],
    );

    if (!configFields || configFields.length === 0) {
      return (
        <AccordionShell
          title={title}
          document={document}
          companyRole={companyRole}
          comments={comments}
          addComment={addComment}
          defaultExpanded={defaultExpanded}
        >
          <Alert severity="error">System is misconfigured.</Alert>
        </AccordionShell>
      );
    }

    return (
      <AccordionShell
        title={title}
        document={document}
        companyRole={companyRole}
        comments={comments}
        addComment={addComment}
        initialValues={initialValues}
        canEditPayload={canEditPayload}
        requiresPayloadSave={mainEntry?.id === ""}
        onSavePayload={canEditPayload ? onSavePayload : undefined}
        onSaveSuccess={onRefreshDocuments}
        defaultExpanded={defaultExpanded}
      >
        {userInfo && (
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            sx={{ marginBottom: 8 }}
          >
            <TextField
              fullWidth
              value={userInfo.phone}
              label="Phone number"
              inputProps={{ disabled: true }}
            />
            <TextField
              fullWidth
              value={userInfo.email}
              label="Email"
              inputProps={{ disabled: true }}
            />
            <TextField
              fullWidth
              value={userInfo.externalEmail}
              label="External system email"
              inputProps={{ disabled: true }}
            />
          </Stack>
        )}
        {mainEntry && (
          <BinaryContent
            allDisabled={!canEditPayload}
            data={mainEntry}
            configFields={configFields}
            fileItems={file}
            requestId={requestId}
            documentInfo={document.document}
          />
        )}
      </AccordionShell>
    );
  },
);
